/**
 * Spec 086/E4 — store of OPEN Wiedervorlagen, shared by `Topbar` (badge) and `Followups`
 * (open-state list). Loaded at app start (`App.svelte` `loadInitialStores`); each mutation goes
 * through this store so the badge updates without a page reload (E4, S8/S9/S17/S20) — no polling.
 */
import { writable } from 'svelte/store';
import { api, type Followup } from '../api';

function createFollowupsStore() {
  const { subscribe, set, update } = writable<Followup[]>([]);

  let loaded = false;

  async function fetchOpen(): Promise<void> {
    const items = await api.followups.list('open');
    set(items);
    loaded = true;
  }

  return {
    subscribe,

    get isLoaded() {
      return loaded;
    },

    /** Initial load (App.svelte) — and also the trigger `Topbar` uses on its own mount. */
    load: fetchOpen,

    /** Re-fetch after a mutation made elsewhere than `complete()`/`reopen()` below. */
    refresh: fetchOpen,

    /** A newly created followup (086/S17) — the create response IS the source of truth. */
    add(item: Followup): void {
      update((items) => [...items, item]);
    },

    /** Sync after an out-of-band delete (e.g. the document-detail card's "remove"). */
    removeLocal(id: string): void {
      update((items) => items.filter((f) => f.id !== id));
    },

    /**
     * Sync after an out-of-band PATCH (Spec 086/F4) — without this, the Topbar badge and the
     * `/followups` list keep showing the pre-edit dueDate/subject until the next full reload.
     * Falls back to appending when the id isn't present yet (defensive; shouldn't happen since
     * only an already-open followup can be edited from the card).
     */
    updateLocal(item: Followup): void {
      update((items) => {
        const index = items.findIndex((f) => f.id === item.id);
        if (index === -1) return [...items, item];
        const next = [...items];
        next[index] = item;
        return next;
      });
    },

    async complete(id: string): Promise<Followup> {
      const updated = await api.followups.complete(id);
      update((items) => items.filter((f) => f.id !== id));
      return updated;
    },

    async reopen(id: string): Promise<Followup> {
      const updated = await api.followups.reopen(id);
      update((items) => [...items, updated]);
      return updated;
    },
  };
}

export const followupsStore = createFollowupsStore();
